import type { AliasEntity } from 'types/playlist'
import type { Track, TrackAlias, TrackAliasValue } from 'types/track'
import type { User } from 'types/user'
import type { UserAliasSettings } from 'types/user-settings'

/**
 * Gets the user alias settings for an entity, with fallback to user's default aliases.
 *
 * Returns the entity's specific user aliases if they exist, otherwise falls back
 * to the user's default metadata aliases.
 *
 * @param entity - The entity (playlist/track) that may have user aliases
 * @param user - The user object containing default metadata aliases
 * @returns The user alias settings to use for this entity
 *
 * @example
 * ```typescript
 * const aliases = getUserAlias(playlist, user)
 * // Returns playlist.user_aliases if exists, otherwise user.metadata_aliases
 * ```
 */
export const getUserAlias = (
  entity: AliasEntity,
  user: User,
): UserAliasSettings => {
  return entity.user_aliases && Object.keys(entity.user_aliases).length > 0
    ? entity.user_aliases
    : user.metadata_aliases
}

/**
 * Merges multiple alias sources with priority order.
 *
 * Combines user aliases, entity aliases, and playlist aliases with playlist aliases
 * taking highest priority, followed by entity aliases, then user aliases.
 *
 * @param entity - The entity that may have user aliases
 * @param user - The user object containing default metadata aliases
 * @param playlistAliases - Playlist-specific alias settings (highest priority)
 * @returns Merged alias settings with proper priority order
 *
 * @example
 * ```typescript
 * const mergedAliases = getMergedAlias(track, user, playlistAliases)
 * // Playlist aliases override entity and user aliases
 * ```
 */
export const getMergedAlias = (
  entity: AliasEntity,
  user: User,
  playlistAliases: UserAliasSettings,
): UserAliasSettings => ({
  ...getUserAlias(entity, user),
  ...playlistAliases,
})

/**
 * Gets the original alias value for a metadata field.
 *
 * Determines the value to use for a metadata field based on parent alias settings.
 * Returns null if the field is excluded, the alias value if specified, or the
 * original track value as fallback.
 *
 * @param parentAlias - Parent alias settings object
 * @param key - The metadata field key to get value for
 * @param originalTrackValue - The original value from the track
 * @returns The value to use (alias value, original value, or null if excluded)
 *
 * @example
 * ```typescript
 * const value = getOriginalAliasValue(aliases, 'Artist', track.artist)
 * // Returns alias value if set, original track.artist if included, or null if excluded
 * ```
 */
export const getOriginalAliasValue = (
  parentAlias: {
    [key: string]: TrackAliasValue
  },
  key: string,
  originalTrackValue?: string | null,
): number | string | null | undefined => {
  if (parentAlias && parentAlias[key]) {
    if (!parentAlias[key].include) return null
    // presense of empty string just means that we must use original track value
    if (parentAlias[key].value) return parentAlias[key].value
  }

  return originalTrackValue
}

/**
 * Gets the effective alias value considering both track-level and parent aliases.
 *
 * Track-level aliases take priority over parent aliases. Returns the track alias
 * value if it exists, otherwise falls back to the parent alias logic.
 *
 * @param parentAlias - Parent-level alias settings
 * @param trackAlias - Track-specific alias settings (higher priority)
 * @param key - The metadata field key to get value for
 * @param originalTrackValue - The original value from the track
 * @returns The effective value to use for this field
 *
 * @example
 * ```typescript
 * const value = getAliasValue(parentAliases, trackAliases, 'Title', track.name)
 * // Track aliases override parent aliases
 * ```
 */
export const getAliasValue = (
  parentAlias: Partial<UserAliasSettings>,
  trackAlias: TrackAlias | null | undefined,
  key: string,
  originalTrackValue?: string | null,
): any | null | undefined => {
  if (trackAlias && trackAlias[key]) return trackAlias[key].value

  return getOriginalAliasValue(parentAlias, key, originalTrackValue)
}

const metadataToKeyMap: {
  [key: string]: string
} = {
  Artwork: 'artwork',
  Title: 'name',
  Artist: 'artist',
  Album: 'album',
}

const applyAlias = (
  track: Track,
  alias: any,
  trackLevelAlias: boolean,
): Track => {
  const resultTrack = Object.keys(alias).reduce((acc, key) => {
    const aliasProp = alias[key]
    if (
      aliasProp &&
      (!aliasProp.include ||
        // set value if alias actually have value
        // (playlist/user level aliases are controlled through include)
        // or if it's track level alias where user can override prop by setting
        // it to empty string
        (aliasProp.include && (aliasProp.value || trackLevelAlias)))
    ) {
      const updatedMetaTrack = {
        ...acc,
        metadata: {
          ...acc.metadata,
          [key]: aliasProp.value,
        },
        // cause campaign creator will take artwork from 160x160px prop
        // that still points to original not aliased artwork, we need to replace
        // it with full size artwork
        // TODO: consult with backend devs about workaround, as artwork can be
        // stretched as in https://app.asana.com/0/35126173840535/819871408439198/f
        artwork_sizes:
          key === 'Artwork' && aliasProp.include && aliasProp.value
            ? {
                full: aliasProp.value,
                '160x160': aliasProp.value,
              }
            : acc.artwork_sizes,
      } as const

      if (metadataToKeyMap[key]) {
        return {
          ...updatedMetaTrack,
          [metadataToKeyMap[key]]: aliasProp.value,
        }
      }

      return updatedMetaTrack
    }

    return acc
  }, track)
  return resultTrack
}

/**
 * Applies alias transformations to an array of tracks.
 *
 * Transforms track metadata based on entity-level aliases and track-specific aliases.
 * Entity aliases apply to all tracks, while track aliases override entity aliases
 * for specific tracks.
 *
 * @param tracks - Array of tracks to apply aliases to
 * @param aliasEntity - Entity containing alias settings and track-specific aliases
 * @param user - User object for fallback alias settings
 * @returns Array of tracks with alias transformations applied
 *
 * @example
 * ```typescript
 * const aliasedTracks = getAliasedTracks(tracks, playlist, user)
 * // Returns tracks with metadata transformed according to alias settings
 * ```
 */
export const getAliasedTracks = (
  tracks: Track[],
  aliasEntity: AliasEntity,
  user: User,
): Track[] => {
  const parentAlias = getMergedAlias(
    aliasEntity,
    user,
    aliasEntity.playlist_aliases,
  )

  const aliasedTracks = tracks.map(track => {
    const trackAlias =
      track.id && aliasEntity.track_aliases
        ? aliasEntity.track_aliases[track.id]
        : null

    const result = applyAlias(track, parentAlias, false)
    if (trackAlias) return applyAlias(result, trackAlias, true)
    return result
  })

  return aliasedTracks
}
